import { h } from 'preact';
import { useState } from 'preact/hooks';

import { IconChevronLeftCircle } from '../../../../../common/icons/icon-chevron-left-circle';
import { IconChevronRightCircle } from '../../../../../common/icons/icon-chevron-right-circle';
import { VideoComponent } from '../../../../../content/components/video/video.component';
import { useTranslation } from '../../../../../i18n';
import { PostAudioPlayer } from '../../../../../music/components/post-audio-player/post-audio-player.component';
import { NeedAccessButton } from '../../../../../need-access/components/need-access-button/need-access-button.component';
import { NeedAccessContentType } from '../../../../../need-access/need-access.interface';
import { buttonize } from '../../../../../utils/a11y.utils';
import type { Post } from '../../../../model/post.interface';
import { PerksHelper } from '../../../../perks.helper';
import { hiddenPostImage } from './hidden-image-base-64';

interface Props {
  post: Post;
  showSlider: boolean;
  showPlayer: boolean;
  showNeedAccessPlayBtn: boolean;
}

export const PerksPostMediaContent = ({
  post,
  showSlider,
  showPlayer,
  showNeedAccessPlayBtn,
}: Props) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startX, setStartX] = useState(0);
  const [endX, setEndX] = useState(0);

  const selectImage = (index) => {
    setCurrentIndex(index);
  };

  const prevImage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(post.contents.length - 1);
    }
  };

  const nextImage = () => {
    if (currentIndex < post.contents.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const onTouchStart = (event) => {
    setStartX(event.touches[0].clientX);
  };

  const onTouchMove = (event) => {
    setEndX(event.touches[0].clientX);
  };

  const onTouchEnd = () => {
    if (startX - endX > 50) {
      nextImage();
    } else if (startX - endX < -50) {
      prevImage();
    }
  };

  if (!post) return null;

  const isProductNoImage =
    PerksHelper.isProduct(post.contents) && !post.contents[0]?.product?.featured_image;

  // An audio post carries an AUDIO content plus an IMAGE artwork content
  const audioContent = PerksHelper.getAudioContent(post.contents);
  const audioArtworkUrl =
    audioContent?.thumbnailUrl ?? PerksHelper.getAudioArtworkUrl(post.contents);

  // @ts-ignore
  return (
    <div class={`single-post-media ${isProductNoImage ? 'single-product-no-image' : ''}`}>
      {/* IMAGE */}
      {PerksHelper.isAllImages(post.contents) && (
        <>
          {post.contents.length === 1 || !showSlider ? (
            <div class="image-container">
              <img src={post.contents[0].url} alt={t('perks.postImageAlt')} />
            </div>
          ) : (
            <div
              class="single-post-image-slider"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div class="single-post-image-slides">
                <div class="image-container">
                  <img src={post.contents[currentIndex].url} alt={t('perks.imageSlideAlt')} />
                </div>
              </div>

              <div class="single-post-image-slider-navigation">
                <IconChevronLeftCircle
                  {...buttonize(prevImage, t('a11y.previousSlide'))}
                  width={18}
                  height={18}
                  fill="#fff"
                />
                <IconChevronRightCircle
                  {...buttonize(nextImage, t('a11y.nextSlide'))}
                  width={18}
                  height={18}
                  fill="#fff"
                />
              </div>

              <div class="single-post-image-slider-bullets">
                {post.contents.map((image, i) => (
                  <span
                    key={i}
                    {...buttonize(
                      () => selectImage(i),
                      t('a11y.goToSlide', { number: String(i + 1) })
                    )}
                    aria-current={i === currentIndex}
                    class={i === currentIndex ? 'active' : ''}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* VIDEO */}
      {PerksHelper.isVideo(post.contents) && (
        <>
          {showPlayer ? (
            <VideoComponent videoId={post.contents[0].id} display="has_access" maxWidth={false} />
          ) : (
            <div class="image-container video-image">
              <img
                src={post.contents[0].thumbnailUrl || post.contents[0].url}
                alt={t('perks.videoThumbnailAlt')}
              />
              {showNeedAccessPlayBtn && (
                <NeedAccessButton
                  contentType={NeedAccessContentType.POST}
                  contentId={post.id}
                  isPlayBtn={true}
                  showLock={false}
                  message={''}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* AUDIO */}
      {PerksHelper.isAudio(post.contents) && audioContent && (
        <>
          {showPlayer ? (
            <PostAudioPlayer
              contentId={audioContent.id}
              postId={post.id}
              playbackId={audioContent.playbackId}
              token={audioContent.token}
              title={audioContent.title}
              artist={audioContent.artist}
              explicit={audioContent.explicit}
              artworkUrl={audioArtworkUrl}
              duration={audioContent.duration}
            />
          ) : showNeedAccessPlayBtn ? (
            <PostAudioPlayer
              contentId={audioContent.id}
              postId={post.id}
              locked={true}
              artworkUrl={audioArtworkUrl ?? hiddenPostImage}
            />
          ) : (
            // Compact contexts (post summaries): artwork thumbnail, same as video
            <div class="image-container audio-image">
              <img src={audioArtworkUrl ?? hiddenPostImage} alt={t('a11y.audioArtwork')} />
            </div>
          )}
        </>
      )}

      {/* PRODUCT */}
      {PerksHelper.isProduct(post.contents) && post.contents[0]?.product?.featured_image && (
        <div class="image-container product-image">
          <img src={post.contents[0].product.featured_image} alt={t('perks.productImageAlt')} />
        </div>
      )}
    </div>
  );
};
